import React, { useEffect, useRef, useState } from 'react';
import { brand } from '../../config/brand';
import { beatAlpha, beatMid, subAlpha, EV } from './story';
import { createScrollDriver } from './scrollDriver';
import type { PainScene } from './painScene';
import './PainStory.css';

/* "Where AI agents break": a full-screen, scroll-driven 3D story that sits
   right after the hero. ~3 scrolls play it through; scrolling back plays it in
   reverse. All copy is real DOM text (in brand.pain); the 3D is decoration. */

const MODEL_URL = `${process.env.PUBLIC_URL}/models/zinify-bot.glb`;
const STILL_URL = `${process.env.PUBLIC_URL}/models/zinify-bot.webp`;

type Beat = (typeof brand.pain.beats)[number];

function track(event: string) {
  const layer = (window as unknown as { dataLayer?: unknown[] }).dataLayer;
  if (Array.isArray(layer)) layer.push({ event });
}

const Source: React.FC<{ beat: Beat }> = ({ beat }) =>
  'source' in beat && beat.source ? (
    <p className="pain__source">
      <cite>
        <a href={beat.source.href} target="_blank" rel="noopener noreferrer">{beat.source.text}</a>
      </cite>
    </p>
  ) : null;

/** One beat's copy. `subRef` collects S2's list items for the one-by-one reveal. */
const BeatCopy: React.FC<{ beat: Beat; index: number; subRef?: (el: HTMLLIElement | null, j: number) => void }> = ({ beat, index, subRef }) => {
  const copy = brand.pain;
  return (
    <>
      {index === 0 ? (
        <>
          <span className="section-label">{copy.label}</span>
          <h2 className="pain__heading" id="pain-heading">{copy.heading}</h2>
        </>
      ) : (
        <h3 className="pain__title">{'title' in beat ? beat.title : ''}</h3>
      )}
      {'items' in beat && beat.items && (
        <ul className="pain__items">
          {beat.items.map((item, j) => (
            <li key={item} ref={subRef ? (el) => subRef(el, j) : undefined}>{item}</li>
          ))}
        </ul>
      )}
      {'lines' in beat && beat.lines && (
        <p className={index === 0 || index === 2 ? 'pain__stat' : 'pain__lines'}>
          {beat.lines.map((l) => <span key={l}>{l}</span>)}
        </p>
      )}
      {'caseLines' in beat && beat.caseLines && (
        <p className="pain__case">{beat.caseLines.map((l) => <span key={l}>{l}</span>)}</p>
      )}
      <Source beat={beat} />
      {index === 3 && (
        <div className="pain__actions">
          <a href="#contact" className="btn btn-primary" id="pain-cta" onClick={() => track('pain_cta')}>
            {copy.cta}
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
          </a>
          <a href="#projects" className="pain__next">{copy.next} ↓</a>
        </div>
      )}
    </>
  );
};

/** Reduced motion: an ordinary section, no pinning, no WebGL. */
const StaticStory: React.FC = () => (
  <section className="pain pain--static section" id="pain" aria-labelledby="pain-heading">
    <div className="container pain__static">
      <img className="pain__static-still" src={STILL_URL} alt="" width={480} height={480} />
      <div className="pain__static-beats">
        {brand.pain.beats.map((beat, i) => (
          <article className="pain__card" key={beat.id}>
            <BeatCopy beat={beat} index={i} />
          </article>
        ))}
      </div>
    </div>
  </section>
);

const ScrollStory: React.FC = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const stageRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const subjectRef = useRef<HTMLDivElement>(null);
  const beatRefs = useRef<(HTMLElement | null)[]>([]);
  const subRefs = useRef<(HTMLLIElement | null)[]>([]);
  const sceneRef = useRef<PainScene | null>(null);
  const travelRef = useRef<() => number>(() => 1);
  const [status, setStatus] = useState<'loading' | 'ready' | 'fallback'>('loading');

  // The driver: progress → overlay styles (imperative, no re-renders) + scene.
  useEffect(() => {
    const section = sectionRef.current, stage = stageRef.current;
    if (!section || !stage) return;
    const touch = window.matchMedia('(pointer: coarse)').matches;
    const lastA: number[] = [];
    const lastS: number[] = [];
    let completed = false;
    const driver = createScrollDriver(section, stage, touch, (p, dp, dt, t) => {
      beatRefs.current.forEach((el, i) => {
        if (!el) return;
        const a = beatAlpha(i, p);
        if (Math.abs(a - (lastA[i] ?? -1)) < 0.002) return;
        lastA[i] = a;
        el.style.opacity = String(a);
        el.style.transform = `translate3d(0, ${(1 - a) * 18}px, 0)`;
        el.style.pointerEvents = a > 0.5 ? 'auto' : 'none';
      });
      subRefs.current.forEach((el, j) => {
        if (!el) return;
        const a = subAlpha(j, p);
        if (Math.abs(a - (lastS[j] ?? -1)) < 0.002) return;
        lastS[j] = a;
        el.style.opacity = String(Math.max(0.12, a));
        el.style.transform = `translate3d(${(1 - a) * -10}px, 0, 0)`;
      });
      if (!completed && p >= EV.complete) { completed = true; track('pain_complete'); }
      sceneRef.current?.frame(p, dp, dt, t);
    });
    travelRef.current = driver.travel;
    return () => driver.dispose();
  }, []);

  // The 3D: imported only when the section comes near.
  useEffect(() => {
    const section = sectionRef.current, host = canvasRef.current;
    if (!section || !host) return;
    let disposed = false;
    const getRegion = () => {
      const s = subjectRef.current?.getBoundingClientRect(), h = host.getBoundingClientRect();
      if (!s || !s.width) return { cx: h.width / 2, cy: h.height / 2, w: h.width, h: h.height };
      return { cx: s.left - h.left + s.width / 2, cy: s.top - h.top + s.height / 2, w: s.width, h: s.height };
    };
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      import('./painScene')
        .then(({ mountPainScene }) => {
          if (disposed) return;
          sceneRef.current = mountPainScene(host, {
            url: MODEL_URL,
            getRegion,
            onReady: () => !disposed && setStatus('ready'),
            onError: () => { if (!disposed) { sceneRef.current?.dispose(); sceneRef.current = null; setStatus('fallback'); } },
          });
        })
        .catch(() => !disposed && setStatus('fallback'));
    }, { rootMargin: '100% 0px' });
    io.observe(section);
    return () => {
      disposed = true;
      io.disconnect();
      sceneRef.current?.dispose();
      sceneRef.current = null;
    };
  }, []);

  // Keyboard: tabbing to a link in a hidden beat scrolls that beat into view.
  const onFocus = (e: React.FocusEvent) => {
    const article = (e.target as HTMLElement).closest('[data-beat]');
    const section = sectionRef.current;
    if (!article || !section) return;
    const i = Number(article.getAttribute('data-beat'));
    const top = section.getBoundingClientRect().top + window.scrollY;
    window.scrollTo({ top: top + beatMid(i) * travelRef.current(), behavior: 'auto' });
  };

  return (
    <section className={`pain pain--scroll pain--${status}`} id="pain" aria-labelledby="pain-heading" ref={sectionRef}>
      <a className="pain__skip" href="#projects">{brand.pain.skip}</a>
      <div className="pain__stage" ref={stageRef} onFocus={onFocus}>
        <div className="pain__canvas" ref={canvasRef} aria-hidden="true" />
        <div className="pain__copy">
          {brand.pain.beats.map((beat, i) => (
            <article
              key={beat.id}
              className={`pain__beat pain__beat--${beat.id}`}
              data-beat={i}
              ref={(el) => { beatRefs.current[i] = el; }}
              style={i === 0 ? undefined : { opacity: 0, pointerEvents: 'none' }}
            >
              <BeatCopy beat={beat} index={i} subRef={(el, j) => { subRefs.current[j] = el; }} />
            </article>
          ))}
        </div>
        <div className="pain__subject" ref={subjectRef} aria-hidden="true">
          {status === 'fallback' && <img className="pain__still" src={STILL_URL} alt="" />}
        </div>
      </div>
    </section>
  );
};

const PainStory: React.FC = () => {
  const [still] = useState(() => window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  return still ? <StaticStory /> : <ScrollStory />;
};

export default PainStory;
